var m = require('mithril');
var prop = require('mithril/stream');
var choices = require('../../libs/choices');
var loaders = require('../../layout/components/loaders');
var modal = require('../../layout/components/modal');

var labelCollator = typeof Intl !== 'undefined' && typeof Intl.Collator === 'function'
  ? new Intl.Collator('en', { numeric: true, sensitivity: 'base' })
  : null;
var locationDescendantSearch = {
  oninit: function (vnode) {
    var ctrl = this;
    ctrl.locationGetter = vnode.attrs.location;
    ctrl.options = prop([]);
    ctrl.selections = prop([]);
    ctrl.isModalOpen = prop(false);
    ctrl.isLoading = prop(false);
    ctrl.errorMessage = prop('');
    ctrl.lastLocationRid = null;
    ctrl.fetchedRid = null;
    ctrl.optionMeta = {};
    ctrl.modalCloseRegistered = false;

    ctrl.handleModalClosed = function () {
      if (!ctrl.isModalOpen()) return;
      ctrl.isModalOpen(false);
      ctrl.selections([]);
      ctrl.modalCloseRegistered = false;
      m.redraw();
    };

    ctrl.renderModalContent = function () {
      if (ctrl.isLoading()) {
        return m('div.flex.items-center.justify-center.pa4', [
          m('img', {
            src: '/images/loading-bars.svg',
            alt: 'Loading',
            style: { width: '3rem', height: 'auto' }
          })
        ]);
      }
      var message = ctrl.errorMessage();
      if (message) {
        return m('div.f5', message);
      }
      var availableOptions = ctrl.options();
      if (!availableOptions.length) {
        return m('div.f5', 'No descendant rooms or equipment found');
      }
      return m('div.descendant-search-choices.w-100', [
        m(choices, {
          data: buildChoicesData(availableOptions),
          value: ctrl.selections,
          placeholderValue: 'Search rooms or zones',
          onchange: ctrl.handleSelectionChange,
          searchResultLimit: 50,
          renderChoiceLimit: Math.min(availableOptions.length, 200)
        })
      ]);
    };

    ctrl.openModal = function () {
      if (ctrl.isModalOpen()) return;
      ctrl.isModalOpen(true);
      if (!ctrl.modalCloseRegistered) {
        ctrl.modalCloseRegistered = true;
        modal.once('closed', ctrl.handleModalClosed);
      }
      modal.vm.title('Search Rooms & Zones');
      modal.vm.body = ctrl.renderModalContent;
      modal.vm.buttons([]);
      modal.vm.open({ extrawidth: true });
      ctrl.buildOptions({ force: true });
    };

    ctrl.buildOptions = async function (options) {
      if (ctrl.isLoading()) return;
      var force = options && options.force === true;
      var record = getLocationRecord(ctrl.locationGetter);
      if (!record || !record['@rid']) {
        ctrl.errorMessage('Location unavailable');
        return;
      }
      var rid = record['@rid'].toString();
      if (!force && ctrl.fetchedRid === rid && ctrl.options().length) return;

      ctrl.isLoading(true);
      m.redraw();
      ctrl.errorMessage('');
      ctrl.options([]);
      ctrl.selections([]);
      ctrl.optionMeta = {};
      try {
        var ridParam = extractRid(rid);
        var data = await loaders.requestWithLoader({
          url: `/api/v1/location/${ridParam}/descendant-search`,
          method: 'GET',
          params: { _: Date.now() },
          background: true
        });
        var rooms = Array.isArray(data && data.rooms) ? data.rooms : [];
        var equipment = Array.isArray(data && data.equipment) ? data.equipment : [];
        equipment = equipment.filter(isZoneEquipment);

        ctrl.optionMeta = {};

        var roomOptions = rooms.map(function (room) {
          var label = formatRoomOptionLabel(room);
          var value = buildSearchableOptionValue(room.rid, label);
          ctrl.optionMeta[value] = {
            type: 'room',
            href: buildLocationHref(room.rid)
          };
          return {
            key: label,
            value: value
          };
        });
        var equipmentOptions = equipment.map(function (entry) {
          var label = formatEquipmentOptionLabel(entry);
          var value = buildSearchableOptionValue(entry.rid, label);
          ctrl.optionMeta[value] = {
            type: 'equipment',
            href: buildEquipmentHref(entry)
          };
          return {
            key: label,
            value: value
          };
        });

        var sortedOptions = sortOptionsAlphabetically(roomOptions.concat(equipmentOptions));
        ctrl.options(sortedOptions);
        ctrl.fetchedRid = rid;
        if (!roomOptions.length && !equipmentOptions.length) {
          ctrl.errorMessage('No descendant rooms or equipment found');
        }
      } catch (error) {
        console.error('Error fetching descendant search data', error);
        ctrl.errorMessage('Unable to load rooms or equipment');
        ctrl.fetchedRid = null;
      } finally {
        ctrl.isLoading(false);
        m.redraw();
      }
    };

    ctrl.handleSelectionChange = function () {
      var selected = ctrl.selections();
      var rid = Array.isArray(selected) ? selected[selected.length - 1] : selected;
      if (!rid) return;
      var meta = ctrl.optionMeta && ctrl.optionMeta[rid];
      if (!meta || !meta.href) return;
      ctrl.errorMessage('');
      ctrl.fetchedRid = null;
      ctrl.selections([]);
      if (ctrl.isModalOpen()) {
        modal.vm.close();
      }
      return m.route.set(meta.href);
    };
  },

  onbeforeupdate: function (vnode) {
    var ctrl = this;
    ctrl.locationGetter = vnode.attrs.location;
    var record = getLocationRecord(ctrl.locationGetter);
    var currentRid = record && record['@rid'] ? record['@rid'].toString() : null;
    if (currentRid !== ctrl.lastLocationRid) {
      ctrl.lastLocationRid = currentRid;
      ctrl.fetchedRid = null;
      ctrl.optionMeta = {};
      ctrl.options([]);
      ctrl.selections([]);
      ctrl.errorMessage('');
      if (ctrl.isModalOpen()) {
        modal.vm.close();
      }
    }
    return true;
  },

  onremove: function () {
    var ctrl = this;
    modal.off('closed', ctrl.handleModalClosed);
    ctrl.modalCloseRegistered = false;
    if (ctrl.isModalOpen()) {
      ctrl.isModalOpen(false);
      modal.vm.close();
    }
  },

  view: function () {
    var ctrl = this;
    return m('div.flex.items-center', [
      m('span.flex.items-center.pointer.blue', {
        role: 'button',
        tabindex: 0,
        title: 'Search rooms and zones',
        onclick: ctrl.openModal,
        onkeypress: function (event) {
          var key = event.key || event.keyCode;
          if (key === 'Enter' || key === ' ' || key === 'Spacebar' || key === 'Space' || key === 13 || key === 32) {
            event.preventDefault();
            ctrl.openModal();
          }
        }
      }, [
        m('i.ion-search', { 
          style: { 
            fontSize: '1.7rem', 
            marginBottom: '0.3rem',
            marginTop: '0.3rem',
          } 
        }),
      ])
    ]);
  }
};

function getLocationRecord(locationGetter) {
  if (!locationGetter) return null;
  return typeof locationGetter === 'function' ? locationGetter() : locationGetter;
}

function extractRid(rid) {
  if (!rid) return '';
  return rid.charAt(0) === '#' ? rid.slice(1) : rid;
}

function buildChoicesData(options) {
  if (!Array.isArray(options) || !options.length) return [];
  return options.concat(options.map(function (option) {
    return {
      key: 'zzzzzzzzzzzzzzzzzzzzzzzzzzzzzz ' + option.key,
      value: option.value
    };
  }));
}

function buildSearchableOptionValue(rid, label) {
  return [rid, label].filter(Boolean).join(' ');
}

function formatRoomDisplayName(room) {
  if (!room) return 'Room';
  var pieces = [];
  if (room.name) pieces.push(room.name);
  if (room.roomNumber) pieces.push(room.roomNumber);
  if (!pieces.length && room.path && room.path.length) {
    pieces.push(room.path[room.path.length - 1].name || 'Room');
  }
  return pieces.join(' ') || 'Room';
}

function formatRoomOptionLabel(room) {
  var baseLabel = room && (room.pathLabel || formatRoomDisplayName(room));
  var labelWithNumber = prependRoomNumberToPath(baseLabel, room && room.roomNumber);
  return labelWithNumber ? `${labelWithNumber} (Room)` : 'Room';
}

function formatEquipmentOptionLabel(entry) {
  if (!entry) return 'Equipment';
  var name = entry.name || entry.class || 'Equipment';
  var roomLabel = entry.path || entry.roomPathLabel || entry.roomLabel || entry.roomName || '';
  var roomNumber = entry.roomNumber;
  if (roomNumber === undefined && entry.room && entry.room.roomNumber !== undefined) {
    roomNumber = entry.room.roomNumber;
  }
  var suffix = roomLabel
    ? prependRoomNumberToPath(roomLabel, roomNumber)
    : prependRoomNumberToPath('', roomNumber);
  var equipmentClass = entry.class || entry['@class'] || 'Equipment';
  return suffix ? `${suffix} – ${name} (${equipmentClass})` : `${name} (${equipmentClass})`;
}

function isZoneEquipment(entry) {
  if (!entry) return false;
  var className = entry.class || entry['@class'] || '';
  return className.toLowerCase().indexOf('zone') !== -1;
}

function prependRoomNumberToPath(label, roomNumber) {
  var number = roomNumber === 0 ? '0' : roomNumber;
  var baseLabel = (label || '').trim();
  if (number === undefined || number === null) {
    return baseLabel;
  }
  var trimmed = String(number).trim();
  if (!trimmed) {
    return baseLabel;
  }
  if (!baseLabel) return trimmed;
  var segments = baseLabel.split('>').map(function (segment) {
    return segment.trim();
  }).filter(Boolean);
  if (!segments.length) {
    return trimmed;
  }
  var lastIndex = segments.length - 1;
  var lastSegment = segments[lastIndex];
  var regex = new RegExp('\\b' + escapeRegExp(trimmed) + '\\b', 'i');
  var cleanedSegment = lastSegment.replace(regex, '').trim();
  var newLastSegment = trimmed + (cleanedSegment ? ' ' + cleanedSegment : '');
  segments[lastIndex] = newLastSegment.trim();
  return segments.join(' > ');
}

function escapeRegExp(value) {
  return value ? value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '';
}

function sortOptionsAlphabetically(options) {
  if (!Array.isArray(options)) return [];
  return options.slice().sort(function (a, b) {
    var labelA = a && a.key ? a.key : '';
    var labelB = b && b.key ? b.key : '';
    return compareLabels(labelA, labelB);
  });
}

function compareLabels(labelA, labelB) {
  var chunksA = tokenizeLabel(labelA);
  var chunksB = tokenizeLabel(labelB);
  var maxLength = Math.max(chunksA.length, chunksB.length);
  for (var i = 0; i < maxLength; i++) {
    var chunkA = chunksA[i];
    var chunkB = chunksB[i];
    if (!chunkA && !chunkB) return 0;
    if (!chunkA) return -1;
    if (!chunkB) return 1;
    if (chunkA.type === chunkB.type) {
      if (chunkA.type === 'number') {
        if (chunkA.value !== chunkB.value) {
          return chunkA.value - chunkB.value;
        }
        continue;
      }
      var textCompare = compareTextChunk(chunkA.value, chunkB.value);
      if (textCompare !== 0) {
        return textCompare;
      }
      continue;
    }
    // Fallback to plain text compare when chunk types differ
    var fallback = compareTextChunk(String(chunkA.raw), String(chunkB.raw));
    if (fallback !== 0) {
      return fallback;
    }
  }
  return 0;
}

function tokenizeLabel(label) {
  if (!label) return [];
  var chunks = [];
  var text = String(label);
  var regex = /\d+/g;
  var lastIndex = 0;
  var match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      var textChunk = text.slice(lastIndex, match.index);
      chunks.push({
        type: 'text',
        value: textChunk,
        raw: textChunk
      });
    }
    var numericValue = parseInt(match[0], 10);
    chunks.push({
      type: 'number',
      value: isNaN(numericValue) ? 0 : numericValue,
      raw: match[0]
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    var tail = text.slice(lastIndex);
    chunks.push({
      type: 'text',
      value: tail,
      raw: tail
    });
  }
  return chunks;
}

function compareTextChunk(valueA, valueB) {
  var textA = valueA || '';
  var textB = valueB || '';
  if (labelCollator) {
    return labelCollator.compare(textA, textB);
  }
  var lowerA = textA.toLowerCase();
  var lowerB = textB.toLowerCase();
  if (lowerA < lowerB) return -1;
  if (lowerA > lowerB) return 1;
  return 0;
}

function buildLocationHref(rid) {
  var id = extractRid(rid);
  return id ? '/location/' + id : null;
}

function buildEquipmentHref(entry) {
  if (!entry || !entry.rid) return null;
  var equipmentClass = entry.class || entry['@class'];
  if (!equipmentClass) return null;
  var id = extractRid(entry.rid);
  if (!id) return null;
  return '/equipment/' + equipmentClass.toLowerCase() + '/' + id;
}

module.exports = locationDescendantSearch;
